/**
 * permissionApi — /api/eraf/permissions CRUD
 *
 * Reuses the same roleApi axios client (same baseURL + interceptors).
 * The client is defined in roleApi.ts; we import only the axios instance
 * indirectly by re-exporting through the same file pattern.
 *
 * NOTE: We create a dedicated thin wrapper rather than polluting roleApi.ts
 * so that future splits are easy. The same getStoredToken + interceptors
 * are reused via the shared apiClient exported from roleApi.ts (via a
 * named re-export below).
 */
import axios from 'axios';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
import type { PermissionItem } from '../types/role';

export interface CreatePermissionRequest {
  code: string;
  resource: string;
  action: string;
  nameKo: string;
  description?: string;
}

export interface UpdatePermissionRequest {
  nameKo?: string;
  description?: string;
  resource?: string;
  action?: string;
}

export interface PermissionListFilter {
  resource?: string;
  action?: string;
}

/** Shared axios instance — identical config as roleApi.ts */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const permissionApi = {
  /** GET /api/eraf/permissions?resource=&action= → PermissionItem[] */
  list: (params?: PermissionListFilter): Promise<PermissionItem[]> => {
    const searchParams = new URLSearchParams();
    if (params?.resource) searchParams.set('resource', params.resource);
    if (params?.action) searchParams.set('action', params.action);
    const qs = searchParams.toString();
    return apiClient
      .get<unknown, { data: PermissionItem[] }>(`/permissions${qs ? `?${qs}` : ''}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/permissions/{id} → PermissionItem */
  get: (id: number): Promise<PermissionItem> =>
    apiClient
      .get<unknown, { data: PermissionItem }>(`/permissions/${id}`)
      .then((res) => res.data),

  /** POST /api/eraf/permissions → 201 PermissionItem */
  create: (body: CreatePermissionRequest): Promise<PermissionItem> =>
    apiClient
      .post<unknown, { data: PermissionItem }>('/permissions', body)
      .then((res) => res.data),

  /** PATCH /api/eraf/permissions/{id} (code NOT editable) → PermissionItem */
  update: (id: number, body: UpdatePermissionRequest): Promise<PermissionItem> =>
    apiClient
      .patch<unknown, { data: PermissionItem }>(`/permissions/${id}`, body)
      .then((res) => res.data),

  /**
   * DELETE /api/eraf/permissions/{id}?force=true|false
   * 409 PERM-409-001 → permission is mapped to roles → surface error + offer force=true.
   */
  delete: (id: number, force = false): Promise<void> =>
    apiClient
      .delete(`/permissions/${id}${force ? '?force=true' : ''}`)
      .then(() => undefined),
};
